import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_BATCH_SIZE = 5;

/**
 * Parameters for {@link useProgressiveExpandedItemsById}.
 */
interface UseProgressiveExpandedItemsByIdParams {
  /**
   * Ordered ids representing the currently visible/eligible items.
   *
   * Changing this list cancels any in-flight progressive expand-all work and
   * prunes expansion state to ids still present in the array.
   */
  itemIds: string[];
  /**
   * Number of additional ids to mark expanded per animation frame while
   * expand-all is in progress.
   *
   * Smaller values reduce main-thread work per frame but lengthen the overall
   * expand-all animation. Larger values complete sooner but can reintroduce UI lag.
   *
   * @default 5
   */
  batchSize?: number;
}

/**
 * Return value of {@link useProgressiveExpandedItemsById}.
 */
interface UseProgressiveExpandedItemsByIdResult {
  /**
   * Expansion state keyed by id for the current item set.
   */
  expandedItems: Record<string, true>;
  /**
   * Toggle expansion for a single id.
   *
   * Any in-flight progressive expand-all work is cancelled first so manual user
   * interaction is never overwritten by queued animation-frame batches.
   */
  toggleExpandedItem: (itemId: string) => void;
  /**
   * Progressively marks all current ids as expanded across animation frames.
   * Already-expanded ids are skipped.
   */
  expandAllItems: () => void;
  /**
   * Immediately collapses all current ids and cancels any in-flight
   * progressive expand-all work.
   */
  collapseAllItems: () => void;
}

/**
 * Manages expansion state by item id and supports progressively expanding all
 * items across multiple animation frames.
 *
 * This is useful for large collapsible card lists where expanding every card in
 * a single state update would mount too much content at once and block the main
 * thread. Manual single-item toggles and collapse-all both cancel any queued
 * expand-all batches so user intent always wins.
 *
 * @param params - Configuration for progressive expand/collapse behavior.
 * @param params.itemIds Ordered ids representing the current visible items.
 * @param params.batchSize Number of ids to expand per animation frame. Defaults to 5.
 *
 * @returns The current `expandedItems` record plus helpers for toggling one id,
 * progressively expanding all ids, and immediately collapsing all ids.
 *
 * @example
 * const {
 *   expandedItems,
 *   toggleExpandedItem,
 *   expandAllItems,
 *   collapseAllItems,
 * } = useProgressiveExpandedItemsById({
 *   itemIds: currentInventories.map((inventory) => inventory.id),
 *   batchSize: 5,
 * });
 */
const useProgressiveExpandedItemsById = ({
  itemIds,
  batchSize = DEFAULT_BATCH_SIZE,
}: UseProgressiveExpandedItemsByIdParams): UseProgressiveExpandedItemsByIdResult => {
  const [expandedItems, setExpandedItems] = useState<Record<string, true>>({});

  const generationRef = useRef(0);
  const frameIdRef = useRef<number | undefined>(undefined);
  const itemIdsRef = useRef(itemIds);
  itemIdsRef.current = itemIds;

  const expandedItemsRef = useRef(expandedItems);
  expandedItemsRef.current = expandedItems;

  const cancelInFlightExpansion = useCallback(() => {
    generationRef.current += 1;

    if (frameIdRef.current !== undefined) {
      cancelAnimationFrame(frameIdRef.current);
      frameIdRef.current = undefined;
    }
  }, []);

  const addExpandedItems = useCallback((idsToAdd: string[]) => {
    if (!idsToAdd.length) {
      return;
    }

    setExpandedItems((previousExpandedItems) => {
      let hasChanges = false;
      const nextExpandedItems = { ...previousExpandedItems };

      idsToAdd.forEach((itemId) => {
        if (!nextExpandedItems[itemId]) {
          nextExpandedItems[itemId] = true;
          hasChanges = true;
        }
      });

      return hasChanges ? nextExpandedItems : previousExpandedItems;
    });
  }, []);

  const scheduleExpansionBatch = useCallback((idsToExpand: string[], startIndex: number, generation: number) => {
    if (startIndex >= idsToExpand.length) {
      frameIdRef.current = undefined;
      return;
    }

    frameIdRef.current = requestAnimationFrame(() => {
      if (generationRef.current !== generation) {
        frameIdRef.current = undefined;
        return;
      }

      const nextEndIndex = startIndex + batchSize;
      addExpandedItems(idsToExpand.slice(startIndex, nextEndIndex));
      scheduleExpansionBatch(idsToExpand, nextEndIndex, generation);
    });
  }, [addExpandedItems, batchSize]);

  useEffect(() => {
    cancelInFlightExpansion();

    setExpandedItems((previousExpandedItems) => {
      if (!itemIds.length) {
        return {};
      }

      const nextExpandedItems = itemIds.reduce<Record<string, true>>((accumulator, itemId) => {
        if (previousExpandedItems[itemId]) {
          accumulator[itemId] = true;
        }

        return accumulator;
      }, {});

      const previousExpandedIds = Object.keys(previousExpandedItems);
      const nextExpandedIds = Object.keys(nextExpandedItems);

      const hasSameExpandedIds = previousExpandedIds.length === nextExpandedIds.length
        && previousExpandedIds.every((itemId) => nextExpandedItems[itemId]);

      return hasSameExpandedIds ? previousExpandedItems : nextExpandedItems;
    });
  }, [itemIds, cancelInFlightExpansion]);

  useEffect(() => {
    return () => {
      cancelInFlightExpansion();
    };
  }, [cancelInFlightExpansion]);

  const toggleExpandedItem = useCallback((itemId: string) => {
    cancelInFlightExpansion();

    setExpandedItems((previousExpandedItems) => {
      const nextExpandedItems = { ...previousExpandedItems };

      if (nextExpandedItems[itemId]) {
        delete nextExpandedItems[itemId];
      } else {
        nextExpandedItems[itemId] = true;
      }

      return nextExpandedItems;
    });
  }, [cancelInFlightExpansion]);

  const expandAllItems = useCallback(() => {
    cancelInFlightExpansion();

    const idsToExpand = itemIdsRef.current.filter((itemId) => !expandedItemsRef.current[itemId]);

    if (!idsToExpand.length) {
      return;
    }

    const generation = generationRef.current;
    addExpandedItems(idsToExpand.slice(0, batchSize));
    scheduleExpansionBatch(idsToExpand, batchSize, generation);
  }, [addExpandedItems, batchSize, cancelInFlightExpansion, scheduleExpansionBatch]);

  const collapseAllItems = useCallback(() => {
    cancelInFlightExpansion();
    setExpandedItems({});
  }, [cancelInFlightExpansion]);

  return {
    expandedItems,
    toggleExpandedItem,
    expandAllItems,
    collapseAllItems,
  };
};

export { useProgressiveExpandedItemsById };
export type {
  UseProgressiveExpandedItemsByIdParams,
  UseProgressiveExpandedItemsByIdResult,
};


